import React, { useEffect, useRef, useState } from "react"
import GoogleMapReact from "google-map-react"
import { GoogleMapsOverlay } from "@deck.gl/google-maps"
import { HeatmapLayer } from "@deck.gl/aggregation-layers"
import PinComponent from "@/components/Common/PinComponent"
import LeafletMainMap from "@/components/core/LeafletMainMap"
import "@/css/enhanced-list-view.css"
import { useVisitsPreferences } from "@/hooks/useLocalStorage"

interface PlaceRecord {
  id: string
  name: string
  image: string
  address?: string
  sector?: string
  city?: string
  region?: string
  country?: string
  assignedTo?: string
  assignedToObj?: {
    firstName: string
    lastName: string
  }
  latitude: number
  longitude: number
  latitudeStart?: number
  longitudeStart?: number
}

interface User {
  id: string
  firstName: string
  lastName: string
}

interface Category {
  id: string
  name: string
}

interface Stage {
  id: string
  name: string
}

interface PlaceStatus {
  id: string
  name: string
}

interface Sector {
  name: string
  count: number
}

interface GeofenceCoordinate {
  latitude: number
  longitude: number
}

interface GeofenceRecord {
  id: string
  organizationId: string
  name: string
  description?: string
  placeId?: string
  geometryJson: string
  sourceFileName?: string
  sourceType?: string
  minLatitude: number
  maxLatitude: number
  minLongitude: number
  maxLongitude: number
  centroidLatitude?: number
  centroidLongitude?: number
  created: string
  pointsCount?: number
  coordinates: Array<{ lat: number; lng: number }>
}

interface GeofenceApiRecord {
  id: string
  organizationId: string
  name: string
  description?: string
  placeId?: string
  geometryJson: string
  sourceFileName?: string
  sourceType?: string
  minLatitude: number
  maxLatitude: number
  minLongitude: number
  maxLongitude: number
  centroidLatitude?: number
  centroidLongitude?: number
  created: string
}

interface GooglePolygonOverlay {
  setMap: (map: unknown) => void
}

import Settings from "@/common/Settings"
import { parsedUser } from "@/common/GetCurrentUser"
import API from "@/common/API"
import moment from "moment"
import { Routes } from "@/routes"
import { useHistory, useParams } from "react-router-dom"
import AssignUser from "@/components/Users/AssignUser"
import { Card, Col, Row } from "react-bootstrap"
import SummaryByStage from "./SummaryByStage"
import AddEditPlace from "./AddEditPlace"
import { useTranslation } from "react-i18next"
import SummaryByStatus from "./SummaryByStatus"
import VisitHistorySidebar from "./VisitHistorySidebar"
import PlacePriority from "./PlacePriority"
import "./VisitHistorySidebar.css"
import SummaryVehicleVisits from "./SummaryVehicleVisits"

const GeofenceMarkerLabel: React.FC<{
  name: string
  onClick: () => void
  lat?: number
  lng?: number
}> = ({ name, onClick }) => (
  <div
    onClick={onClick}
    style={{
      background: "rgba(26, 26, 26, 0.92)",
      color: "#fff",
      border: "2px solid #fff",
      borderRadius: "14px",
      padding: "7px 12px",
      fontSize: "12px",
      fontWeight: 700,
      whiteSpace: "nowrap",
      boxShadow: "0 4px 12px rgba(0,0,0,0.28)",
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      transform: "translate(-50%, -50%)",
    }}
    title={name}
  >
    <span
      style={{
        width: "10px",
        height: "10px",
        borderRadius: "50%",
        background: "#ff7a00",
        boxShadow: "0 0 0 3px rgba(255,122,0,0.28)",
        display: "inline-block",
        flex: "0 0 auto",
      }}
    />
    {name}
  </div>
)

const Visits: React.FC = () => {
  const history = useHistory()
  const { t } = useTranslation("global")
  const heatmapRef = useRef(null);
  const isInitialMount = useRef(true); // Track initial mount to avoid duplicate requests
  // Use localStorage for user preferences
  const {
    displayMode,
    setDisplayMode,
    showFilter,
    setShowFilter,
    filter,
    setFilter,
    showFinished,
    setShowFinished,
    showTrucks,
    setShowTrucks,
    mapProvider,
  } = useVisitsPreferences()

  const [quickActionsOpen, setQuickActionsOpen] = useState(false)
  const quickActionsRef = useRef<HTMLDivElement | null>(null)

  // Local component states
  const [showHistory, setShowHistory] = useState(false)
  const [showPriority, setShowPriority] = useState(false)
  const [mapFiltersExpanded, setMapFiltersExpanded] = useState(true)
  const [polygonToolsExpanded, setPolygonToolsExpanded] = useState(false)
  const [showPoints, setShowPoints] = useState(true)
  const [recordsVehicles, setRecordsVehicles] = useState([])
  const user = parsedUser()
  const [records, setRecords] = useState<PlaceRecord[]>([])
  const [visibleRecords, setVisibleRecords] = useState<PlaceRecord[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [loadingProgress, setLoadingProgress] = useState(0)
  const [isMobile, setIsMobile] = useState(false)
  const [totalRecords, setTotalRecords] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize] = useState(100) // Cargar de 100 en 100
  const [requestSize, setRequestSize] = useState<string>('') // Para debug del tamaño de la petición
  const GoogleMapRef = useRef(null)
  const [mapMode, setMapMode] = useState<string | null>(null) //This is used to set the mode of the mode of the map; null = nothing; drawPolygon = allow to draw
  const [thePolygon, setThePolygon] = useState(null)
  const activeFilterCount = Object.values(filter).filter(val => val !== null && val !== "").length
  const isShowFinishedInitial = useRef(true)
  const isAutoFilterInitial = useRef(true)
  const isMapView = displayMode === "map"

  useEffect(() => {
    if (!quickActionsOpen) {
      return
    }

    const handleClick = (event: MouseEvent | TouchEvent) => {
      if (!quickActionsRef.current) {
        return
      }
      if (!quickActionsRef.current.contains(event.target as Node)) {
        setQuickActionsOpen(false)
      }
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setQuickActionsOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClick, true)
    document.addEventListener("touchstart", handleClick, true)
    document.addEventListener("keydown", handleKeyDown)
    return () => {
      document.removeEventListener("mousedown", handleClick, true)
      document.removeEventListener("touchstart", handleClick, true)
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [quickActionsOpen])

  // Modal toggle functions
  const togglePriorityModal = () => {
    setShowPriority(!showPriority)
  }
  const toggleHistoryModal = () => {
    setShowHistory(!showHistory)
  }
  const [state, setState] = useState({
    center: { lat: 18.479777, lng: -69.9341112 },
    zoom: 10,
    Devices: [],
    Pins: [],
    Geofences: [],
    DeviceSelected: null,
    Events: [],
    UI: {
      showAddEditGeofence: false,
      GeofenceSelected: null,
    },
  })
  const [theMap, setTheMap] = useState(null)
  const [theMapsReference, setTheMapsReference] = useState(null)
  const geofencePolygonsRef = useRef<GooglePolygonOverlay[]>([])
  const [zoom] = useState(19)
  const [theHeatMapOpts, setTheHeatMapOpts] = useState({
    positions: [],
    options: {
      radius: 30,
      opacity: 0.6,
    },
  })

  // Detect touch device and tablet optimization
  useEffect(() => {
    const checkDevice = () => {
      const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0
      const isTablet = window.innerWidth >= 768 && window.innerWidth <= 1024
      setIsMobile(isTouch && isTablet)
    }

    checkDevice()
    window.addEventListener('resize', checkDevice)
    return () => window.removeEventListener('resize', checkDevice)
  }, [])

  const handleApiLoaded = (map, mapsReference) => {
    setTheMap(map)
    setTheMapsReference(mapsReference)

    // Optimize for touch devices
    if (isMobile) {
      map.setOptions({
        gestureHandling: 'greedy',
        zoomControl: true,
        zoomControlOptions: {
          position: mapsReference.ControlPosition.RIGHT_BOTTOM
        },
        streetViewControl: false,
        fullscreenControl: false,
        mapTypeControl: false
      })
    }
  }

  const centerMap = () => {
    if (avoidAutoZoom === true) {
      return
    }
    if (theMapsReference) {
      const bounds = new theMapsReference.LatLngBounds()

      records.forEach(marker => {
        if (marker.latitude) {
          bounds.extend(
            new theMapsReference.LatLng(marker.latitude, marker.longitude)
          )
        }
      })

      theMap.fitBounds(bounds)
      //theMap.setZoom(zoom);
    }
  }
  if (state.center === undefined) {
  }

  const toggleDetails = obj => {

    if (obj.idCoche) {
      history.push(Routes.VehicleDetails.clean + obj.idCoche)
    } else {
      history.push(Routes.PlaceDetails.clean + obj.id)
    }
  }
  const [sectors, setSectors] = useState<Sector[]>([])
  const requestSectors = async () => {
    let Query = "OrganizationId=" + user.OrganizationId
    if (filter.from) {
      Query += `&from=${moment(filter.from).format(
        "YYYY-MM-DD"
      )}&until=${moment(filter.until).format("YYYY-MM-DD")}`
    }
    if (filter.sector) {
      Query += `&sector=${filter.sector}`
    }
    if (filter.name) {
      Query += `&name=${filter.name} `
    }

    //   var response = await Http.GetJsonAsync<pleelo.Helpers.APIModelRequestHelper>("/api/Expense/List?" + Query);
    let requestAPI = await API.getAction("place/sectorsWithCount", Query)

    if (requestAPI.data.status === "ok") {
      const result = requestAPI.data.response
      setSectors(result)
    }
  }

  // Main request with optimization
  const request = async (avoidZoom?: boolean, loadMore = false) => {
    if (avoidZoom) {
      setAvoidAutoZoom(true)
    }
    if (!user) {
      history.push(Routes.SignIn.path)
      return
    }

    setIsLoading(true)
    setLoadingProgress(0)

    try {
      let Query = "OrganizationId=" + user.OrganizationId
      if (filter.from) {
        Query += `&from=${moment(filter.from).format(
          "YYYY-MM-DD"
        )}&until=${moment(filter.until).format("YYYY-MM-DD")}`
      }
      if (filter.sector) {
        Query += `&sector=${filter.sector}`
      }
      if (filter.stageId) {
        Query += `&stageId=${filter.stageId}`
      }
      if (filter.categoryId) {
        Query += `&categoryId=${filter.categoryId} `
      }

      if (filter.placeStatusId) {
        Query += `&placeStatusId=${filter.placeStatusId} `
      }

      if (filter.assignedTo) {
        Query += `&assignedTo=${filter.assignedTo} `
      }
      if (filter.withVehicleVisits) {
        Query += `&withVehicleVisits=${filter.withVehicleVisits}`
      }

      Query += `&showFinished=${showFinished} `

      // Optimización inteligente según el modo de vista


      setLoadingProgress(30)

      let requestAPI = await API.getAction("place/list", Query)

      setLoadingProgress(70)

      if (requestAPI.data.status === "ok") {
        // Calcular tamaño aproximado del response para monitoreo
        const responseSize = new Blob([JSON.stringify(requestAPI.data)]).size
        const sizeInKB = (responseSize / 1024).toFixed(2)
        setRequestSize(`${sizeInKB} KB`)

        // Log en desarrollo para monitorear
        if (process.env.NODE_ENV === 'development') {
          console.log(`📦 Place list response size: ${sizeInKB} KB (${requestAPI.data.response.length} records)`)
        }

        const result = requestAPI.data.response
        const total = requestAPI.data.total || result.length

        setTotalRecords(total)

        // En modo mapa, siempre reemplazar con todos los registros
        setRecords(result)
        setVisibleRecords(result)
        setCurrentPage(1)

      }

      setLoadingProgress(100)
    } catch (error) {
      console.error("Error loading places:", error)
    } finally {
      setTimeout(() => {
        setIsLoading(false)
        setLoadingProgress(0)
      }, 300)
    }
  }
  const [placeStatus, setPlaceStatus] = useState<PlaceStatus[]>([])
  const requestStatus = async () => {
    let Query = "OrganizationId=" + user.OrganizationId

    //   var response = await Http.GetJsonAsync<pleelo.Helpers.APIModelRequestHelper>("/api/Expense/List?" + Query);
    let requestAPI = await API.getAction("placeStatus/list", Query)

    if (requestAPI.data.status === "ok") {
      const result = requestAPI.data.response
      setPlaceStatus(result)
    }
  }
  const [stages, setStages] = useState<Stage[]>([])
  const requestStages = async () => {
    let requestAPI = await API.getAction(
      "Stage/List?OrganizationId=" + user.OrganizationId
    )

    if (requestAPI.data.status === "ok") {
      setStages(requestAPI.data.response)
    }
  }

  const [categories, setCategories] = useState<Category[]>([])
  const requestCategories = async () => {
    let requestAPI = await API.getAction(
      "category/List?OrganizationId=" + user.OrganizationId
    )

    if (requestAPI.data.status === "ok") {
      setCategories(requestAPI.data.response)
    }
  }

  const [users, setUsers] = useState<User[]>([])
  const requestUsers = async () => {
    let requestAPI = await API.getAction(
      "users/List?OrganizationId=" + user.OrganizationId
    )

    if (requestAPI.data.status === "ok") {
      setUsers(requestAPI.data.response)
    }
  }
  const requestTrucks = async () => {
    let requestAPI = await API.getAction("agl/carLastPosition?OrganizationId=" + user.OrganizationId)

    if (requestAPI.data.status === "ok") {
      setRecordsVehicles(requestAPI.data.response)
    }
  }

  const requestGeofences = async () => {
    if (!user?.OrganizationId) {
      return
    }

    try {
      const requestAPI = await API.getAction(
        "Geofences/List",
        "OrganizationId=" + user.OrganizationId
      )

      if (requestAPI.data.status === "ok") {
        const geofences = ((requestAPI.data.response || []) as GeofenceApiRecord[]).map(item => {
          let coordinates: Array<{ lat: number; lng: number }> = []

          try {
            const parsedCoordinates = JSON.parse(item.geometryJson || "[]")
            if (Array.isArray(parsedCoordinates)) {
              coordinates = parsedCoordinates
                .filter(
                  (point: GeofenceCoordinate) =>
                    typeof point?.latitude === "number" &&
                    typeof point?.longitude === "number"
                )
                .map((point: GeofenceCoordinate) => ({
                  lat: point.latitude,
                  lng: point.longitude,
                }))
            }
          } catch (error) {
            coordinates = []
          }

          return {
            ...item,
            pointsCount: coordinates.length,
            coordinates,
          } as GeofenceRecord
        })

        setState(prev => ({
          ...prev,
          Geofences: geofences,
        }))
      }
    } catch (error) {
      console.error("Error loading geofences:", error)
    }
  }

  const vehicleInterval = useRef(null)

  const toggleVehicles = () => {
    console.log("toggle vehicles")
    setShowTrucks(!showTrucks)
  }

  const toggleDispatchFilter = () => {
    const currentValue = filter.withVehicleVisits ?? ""
    const nextValue =
      currentValue === "" ? "true" : currentValue === "true" ? "false" : ""

    setFilter({
      ...filter,
      withVehicleVisits: nextValue,
    })
  }

  useEffect(() => {
    if (showTrucks) {
      //means is goint to change to true
      requestTrucks()
      vehicleInterval.current = setInterval(() => {
        console.log("trigger vehicle interval", showTrucks)
        if (showTrucks) {
          requestTrucks()
        }
      }, 60000)
    } else {
      console.log("trigger vehicle interval to stop")
      setRecordsVehicles([])
      clearInterval(vehicleInterval.current)
    }
  }, [showTrucks])
  const [polygon, setPolygon] = useState([])
  const [avoidAutoZoom, setAvoidAutoZoom] = useState(false)
  const [selectedPlaces, setSelectedPlaces] = useState<PlaceRecord[]>([])

  const goToGeofenceDetails = (geofence: GeofenceRecord) => {
    history.push(Routes.GeofenceDetails.clean + geofence.id)
  }

  //Read all the markers between the polygon definition
  const getPlacesByPolygon = () => {
    const places: PlaceRecord[] = []
    records.forEach(item => {
      let exist = theMapsReference.geometry.poly.containsLocation(
        { lat: item.latitude, lng: item.longitude },
        thePolygon
      )

      if (exist) {
        places.push(item)
      }
    })

    setSelectedPlaces(places)
  }
  const handleClickOnMap = (obj: { lat: number; lng: number }) => {
    if (mapMode === "drawPolygon") {
      setPolygon([
        ...polygon,
        {
          lat: obj.lat,
          lng: obj.lng,
        },
      ])
    }
  }

  useEffect(() => {
    if (records.length === 0) {
      return
    }
    const points: { lat: number; lng: number }[] = []
    const pins: Array<{ PositionType: string; latitude: number; longitude: number; item: PlaceRecord }> = []
    records.forEach(item => {
      points.push({
        lat: item.latitudeStart || item.latitude,
        lng: item.longitudeStart || item.longitude,
      })

      pins.push({
        PositionType: "Visita",
        latitude: item.latitudeStart || item.latitude,
        longitude: item.longitudeStart || item.longitude,
        item: item,
      })
    })
    setState({
      ...state,
      Pins: pins,
    })
    setTheHeatMapOpts({
      ...theHeatMapOpts,
      positions: points,
    })

    if (records && records.length > 0 && avoidAutoZoom === false) {
      centerMap()
    } else {
      setAvoidAutoZoom(false)
    }
  }, [records])

  useEffect(() => {
    if (mapMode === null) {
      setPolygon([])

      setSelectedPlaces([])
    } else if (mapMode === "drawPolygon") {
    }
  }, [mapMode])

  useEffect(() => {
    if (polygon.length > 0) {
      thePolygon?.setMap(null)
      if (theMapsReference) {
        setThePolygon(
          new theMapsReference.Polygon({
            paths: polygon,
            strokeColor: "#FF0000",
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: "#FF0000",
            fillOpacity: 0.35,
          })
        )
      }
    } else {
      thePolygon?.setMap(null)
    }
  }, [polygon])

  useEffect(() => {
    thePolygon?.setMap(theMap)
  }, [thePolygon])

  useEffect(() => {
    geofencePolygonsRef.current.forEach(polygon => {
      theMapsReference?.event?.clearInstanceListeners?.(polygon)
      polygon.setMap(null)
    })
    geofencePolygonsRef.current = []

    if (!theMap || !theMapsReference || mapProvider === "osm") {
      return
    }

    const overlays = (state.Geofences as GeofenceRecord[])
      .filter(geofence => geofence.coordinates.length >= 3)
      .flatMap(geofence => {
        const haloPolygon = new theMapsReference.Polygon({
          paths: geofence.coordinates,
          strokeColor: "#ffffff",
          strokeOpacity: 0.95,
          strokeWeight: 8,
          fillOpacity: 0,
          clickable: true,
          zIndex: 998,
        })

        const polygon = new theMapsReference.Polygon({
          paths: geofence.coordinates,
          strokeColor: "#ff7a00",
          strokeOpacity: 1,
          strokeWeight: 4,
          fillColor: "#ff9f43",
          fillOpacity: 0.24,
          clickable: true,
          zIndex: 999,
        })

        haloPolygon.setMap(theMap)
        polygon.setMap(theMap)

        const highlight = () => {
          polygon.setOptions({
            strokeWeight: 5,
            fillOpacity: 0.34,
          })
        }

        const unhighlight = () => {
          polygon.setOptions({
            strokeWeight: 4,
            fillOpacity: 0.24,
          })
        }

        polygon.addListener("click", () => goToGeofenceDetails(geofence))
        polygon.addListener("mouseover", highlight)
        polygon.addListener("mouseout", unhighlight)
        haloPolygon.addListener("click", () => goToGeofenceDetails(geofence))

        return [haloPolygon, polygon]
      })

    geofencePolygonsRef.current = overlays

    return () => {
      overlays.forEach(polygon => {
        theMapsReference?.event?.clearInstanceListeners?.(polygon)
        polygon.setMap(null)
      })
    }
  }, [mapProvider, state.Geofences, theMap, theMapsReference])

  // Initial data load - consolidated to avoid duplicate requests
  useEffect(() => {
    // Only load initial data if not in a detail view
    if (!chatId && !placeId) {
      request()
      requestSectors()
      requestStages()
      requestCategories()
      requestUsers()
      requestStatus()
      requestGeofences()
    }
  }, [])

  // Reload when showFinished changes (skip initial mount to avoid duplicate request)
  useEffect(() => {
    if (isShowFinishedInitial.current) {
      isShowFinishedInitial.current = false
      return
    }
    request()
  }, [showFinished])

  useEffect(() => {
    if (isAutoFilterInitial.current) {
      isAutoFilterInitial.current = false
      return
    }

    const typedName = (filter.name || "").trim()
    if (typedName.length > 0 && typedName.length < 3) {
      return
    }

    const debounce = setTimeout(() => {
      request(true)
    }, 350)

    return () => clearTimeout(debounce)
  }, [filter])

  const [showAssignUsers, setShowAssignUsers] = useState(false)
  const [selectedPlace, setSelectedPlace] = useState<PlaceRecord | null>(null)
  const toggleAssignUsers = (obj?: PlaceRecord) => {
    setShowAssignUsers(!showAssignUsers)
    setSelectedPlace(obj || null)
    if (showAssignUsers) {
      request(true)
    }
  }
  const [showAddEditPlace, setShowAddEditPlace] = useState(false)
  const toggleAddEditPlace = () => {
    setShowAddEditPlace(!showAddEditPlace)
  }
  const { chatId, placeId } = useParams()
  const [showHeatMap, setShowHeatMap] = useState(false)
  const toggleHeatMap = async () => {
    if (!isMapView) {
      setDisplayMode("map")
      return
    }
    // Google removed maps.visualization.HeatmapLayer in May 2026 (Maps JS API v3.65);
    // the heatmap is now rendered with deck.gl over the Google map instance.
    if (mapProvider !== "google" || !theMap) {
      return
    }
    setShowHeatMap(!showHeatMap)
    if (showHeatMap == false) {

      let dataRequest = await API.getAction("vehicleVisits/heatMap", "OrganizationId=" + user.OrganizationId);
      if (dataRequest.data.status === "ok") {
        let data = dataRequest.data.response;
        let heatmapData = data
          .filter(x => x.latitude > 0)
          .slice(0, isMobile ? 20 : data.length) // Limit vehicle markers on mobile
          .map((item) => {
            return ({
              lat: item.latitude,
              lng: item.longitude
            }

            )
          })
        const overlay = new GoogleMapsOverlay({
          layers: [
            new HeatmapLayer({
              id: "vehicle-heatmap",
              data: heatmapData,
              getPosition: (d: any) => [d.lng, d.lat],
              radiusPixels: 70,
              intensity: 4,
              threshold: 0.02,
              opacity: 0.85,
            }),
          ],
        });
        overlay.setMap(theMap);
        heatmapRef.current = overlay;
      }


    } else {
      if (heatmapRef.current) {
        heatmapRef.current.setMap(null);
        heatmapRef.current = null;
      }
    }
  }

  // Reload when navigating back from detail views
  useEffect(() => {
    // Skip on initial mount to avoid duplicate request
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Only reload if we're coming back from a detail view (chatId or placeId was set, now it's cleared)
    if (!chatId && !placeId) {
      request(true)
    }
  }, [chatId, placeId])

  const ListOfPlacesUI = (records: PlaceRecord[], customStyle: React.CSSProperties = {}) => {
    const containerStyle: React.CSSProperties = {
      overflow: "auto",
      height: "83vh",
      backgroundColor: "#f8f9fa",
      ...customStyle,
    }

    if (!records || records.length === 0) {
      return (
        <div
          className="d-flex flex-column align-items-center justify-content-center p-4"
          style={containerStyle}
        >
          <i className="fa fa-map-marker-alt fa-3x text-muted mb-3"></i>
          <h5 className="text-muted">{t("No places found")}</h5>
          <p className="text-muted text-center">{t("Try adjusting your filters or add a new place")}</p>
          <button
            className="btn btn-primary"
            onClick={() => toggleAddEditPlace()}
          >
            <i className="fa fa-plus me-2"></i>
            {t("Add New Place")}
          </button>
        </div>
      )
    }

    return (
      <div style={containerStyle} className="p-2">
        {/* Header with count and actions */}
        <div className="d-flex justify-content-between align-items-center mb-3 px-2">
          <div>
            <h6 className="mb-0">
              {t("Places")} <span className="text-muted">({records.length})</span>
              {totalRecords > records.length && (
                <small className="text-muted ms-2">
                  ({t("of")} {totalRecords})
                </small>
              )}
            </h6>
            {/* Indicador de tamaño en desarrollo */}
            {process.env.NODE_ENV === "development" && requestSize && (
              <small className="text-info d-block mt-1">
                <i className="fa fa-database me-1"></i>
                {t("Response size")}: {requestSize}
              </small>
            )}
          </div>
          <div className="btn-group btn-group-sm">
            <button
              className="btn btn-outline-primary"
              onClick={() => toggleAddEditPlace()}
              title={t("Add new place")}
            >
              <i className="fa fa-plus"></i>
            </button>
          </div>
        </div>

        {/* Places grid */}
        <div className="row g-3">
          {records.map((item, index) => {
            const hasImage = item.image && item.image.trim() !== ''
            const assignedUser = item.assignedToObj?.firstName || null

            return (
              <div className="col-12 col-md-6 col-lg-4" key={`place-${item.id || index}`}>
                <div className="card h-100 shadow-sm border-0 hover-shadow transition-all">
                  {/* Image section */}
                  <div
                    className="card-img-top position-relative overflow-hidden"
                    style={{ height: "200px", backgroundColor: "#e9ecef" }}
                  >
                    {hasImage ? (
                      <img
                        src={`${Settings.BasePathForAPI}file/getThumbnail?filename=${item.image}`}
                        loading="lazy"
                        alt={item.name}
                        className="w-100 h-100 object-fit-cover cursor-pointer"
                        onClick={() => toggleDetails(item)}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement
                          target.style.display = 'none'
                          target.nextElementSibling?.classList.remove('d-none')
                        }}
                      />
                    ) : null}

                    {/* Fallback icon */}
                    <div className={`d-flex align-items-center justify-content-center h-100 ${hasImage ? 'd-none' : ''}`}>
                      <i className="fa fa-building fa-3x text-muted"></i>
                    </div>

                    {/* Status badges */}
                    <div className="position-absolute top-0 start-0 p-2">
                      {assignedUser && (
                        <span className="badge bg-success rounded-pill">
                          <i className="fa fa-user me-1"></i>
                          {t("Assigned")}
                        </span>
                      )}
                    </div>

                    {/* Action overlay */}
                    <div className="position-absolute top-0 end-0 p-2">
                      <div className="btn-group-vertical">
                        <button
                          className="btn btn-light btn-sm rounded-circle shadow-sm"
                          onClick={(e) => {
                            e.stopPropagation()
                            toggleDetails(item)
                          }}
                          title={t("View details")}
                        >
                          <i className="fa fa-eye"></i>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Content section */}
                  <div className="card-body p-3">
                    {/* Title */}
                    <h6 className="card-title mb-2 fw-bold text-truncate" title={item.name}>
                      {item.name}
                    </h6>

                    {/* Address */}
                    <div className="text-muted small mb-2">
                      <i className="fa fa-map-marker-alt me-1 text-danger"></i>
                      <span className="text-truncate d-inline-block" style={{ maxWidth: "200px" }}>
                        {[item.address, item.sector, item.city, item.region, item.country]
                          .filter(Boolean)
                          .join(", ") || t("No address provided")}
                      </span>
                    </div>

                    {/* Additional info */}
                    <div className="row g-2 mb-3">
                      {item.sector && (
                        <div className="col-12">
                          <small className="text-muted">
                            <i className="fa fa-tag me-1"></i>
                            <strong>{t("Sector")}:</strong> {item.sector}
                          </small>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Footer actions */}
                  <div className="card-footer bg-white border-0 pt-0">
                    <div className="d-flex gap-2">
                      <button
                        className={`btn btn-sm flex-fill ${assignedUser ? 'btn-outline-primary' : 'btn-primary'}`}
                        onClick={(e) => {
                          e.stopPropagation()
                          toggleAssignUsers(item)
                        }}
                      >
                        <i className={`fa ${assignedUser ? 'fa-user-edit' : 'fa-user-plus'} me-1`}></i>
                        {assignedUser || t("Assign")}
                      </button>

                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={(e) => {
                          e.stopPropagation()
                          toggleDetails(item)
                        }}
                        title={t("View details")}
                      >
                        <i className="fa fa-arrow-right"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }
  const totalActiveFiltersCount =
    activeFilterCount +
    (showHeatMap ? 1 : 0) +
    (showTrucks ? 1 : 0) +
    (!showFinished ? 1 : 0) +
    (!showPoints ? 1 : 0)

  const filterUI = () => {
    return (
      <div className="p-3">
        <div className="mb-4 p-3 rounded-3" style={{ background: "rgba(255,255,255,0.08)" }}>
          <button
            type="button"
            className="btn btn-link text-white text-decoration-none p-0 mb-3 w-100 d-flex justify-content-between align-items-center"
            onClick={() => setMapFiltersExpanded(!mapFiltersExpanded)}
          >
            <span className="fw-bold">
              <i className="fa fa-sliders-h me-2"></i>
              {t("Map")}
            </span>
            <i className={`fa ${mapFiltersExpanded ? "fa-chevron-up" : "fa-chevron-down"}`}></i>
          </button>
          {mapFiltersExpanded && (
            <div className="d-flex flex-column gap-2">
              <button
                type="button"
                className={`btn btn-sm text-start d-flex justify-content-between align-items-center ${showPoints ? "btn-primary" : "btn-outline-light"}`}
                onClick={() => setShowPoints(!showPoints)}
              >
                <span>
                  <i className="fa fa-map-marker-alt me-2"></i>
                  {t("Points")}
                </span>
                <span className={`badge ${showPoints ? "bg-light text-dark" : "bg-secondary"}`}>
                  {showPoints ? t("On") : t("Off")}
                </span>
              </button>

            <button
              type="button"
              className={`btn btn-sm text-start d-flex justify-content-between align-items-center ${showHeatMap ? "btn-danger" : "btn-outline-light"}`}
              onClick={() => toggleHeatMap()}
            >
              <span>
                <i className="fa fa-thermometer-half me-2"></i>
                {t("Heatmap")}
              </span>
              <span className={`badge ${showHeatMap ? "bg-light text-dark" : "bg-secondary"}`}>
                {showHeatMap ? t("On") : t("Off")}
              </span>
            </button>

            <button
              type="button"
              className={`btn btn-sm text-start d-flex justify-content-between align-items-center ${showTrucks ? "btn-success" : "btn-outline-light"}`}
              onClick={() => toggleVehicles()}
            >
              <span>
                <i className="fa fa-truck me-2"></i>
                {t("Vehicles")}
              </span>
              <span className={`badge ${showTrucks ? "bg-light text-dark" : "bg-secondary"}`}>
                {showTrucks ? t("On") : t("Off")}
              </span>
            </button>

            <button
              type="button"
              className={`btn btn-sm text-start d-flex justify-content-between align-items-center ${!showFinished ? "btn-info text-dark border-0" : "btn-outline-light"}`}
              onClick={() => setShowFinished(!showFinished)}
            >
              <span>
                <i className="fa fa-flag-checkered me-2"></i>
                {t("Hide")} {t("Finished")}
              </span>
              <span className={`badge ${!showFinished ? "bg-dark text-white" : "bg-secondary"}`}>
                {!showFinished ? t("On") : t("Off")}
              </span>
            </button>
              <button
                type="button"
                className={`btn btn-sm text-start d-flex justify-content-between align-items-center ${filter.withVehicleVisits ? "btn-warning text-dark border-0" : "btn-outline-light"}`}
                onClick={toggleDispatchFilter}
              >
                <span>
                  <i className="fa fa-truck me-2"></i>
                  {t("Con despacho")}
                </span>
                <span className={`badge ${filter.withVehicleVisits ? "bg-dark text-white" : "bg-secondary"}`}>
                  {filter.withVehicleVisits === "true"
                    ? t("Si")
                    : filter.withVehicleVisits === "false"
                      ? t("No")
                      : t("Todos")}
                </span>
              </button>

              <div className="mt-2 pt-2 border-top border-secondary">
                <button
                  type="button"
                  className="btn btn-link text-white text-decoration-none p-0 mb-2 w-100 d-flex justify-content-between align-items-center"
                  onClick={() => setPolygonToolsExpanded(!polygonToolsExpanded)}
                >
                  <span className="small fw-semibold">
                    <i className="fa fa-draw-polygon me-2"></i>
                    {t("Select by Polygon")}
                  </span>
                  <i className={`fa ${polygonToolsExpanded ? "fa-chevron-up" : "fa-chevron-down"}`}></i>
                </button>

                {polygonToolsExpanded && (
                  <div className="d-flex flex-column gap-2">
                    <button
                      type="button"
                      className={`btn btn-sm text-start d-flex justify-content-between align-items-center ${mapMode === "drawPolygon" ? "btn-light text-dark" : "btn-outline-light"}`}
                      onClick={() => {
                        setMapMode(mapMode === "drawPolygon" ? null : "drawPolygon")
                      }}
                    >
                      <span>
                        <i className="fa fa-crosshairs me-2"></i>
                        {mapMode === "drawPolygon" ? t("Drawing enabled") : t("Start polygon selection")}
                      </span>
                    </button>

                    {mapMode === "drawPolygon" && (
                      <>
                        <small className="text-light opacity-75">
                          {t("Tap on map to add polygon points")}
                        </small>
                        <div className="d-flex gap-2">
                          <button
                            type="button"
                            className="btn btn-sm btn-secondary w-100"
                            onClick={getPlacesByPolygon}
                          >
                            {t("Get Places")}
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-light"
                            onClick={() => setMapMode(null)}
                          >
                            {t("Clear")}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Date Range Filter */}
        <div className="mb-4">
          <label className="form-label fw-bold text-white">
            <i className="fa fa-calendar me-2"></i>
            {t("Date Range")}
          </label>
          <div className="row g-2">
            <div className="col-6">
              <label className="form-label small text-light">{t("From")}</label>
              <input
                type="date"
                className="form-control form-control-sm"
                id="from"
                value={filter.from || ""}
                onChange={e => {
                  setFilter({
                    ...filter,
                    from: e.target.value,
                  })
                }}
              />
            </div>
            <div className="col-6">
              <label className="form-label small text-light">{t("To")}</label>
              <input
                type="date"
                className="form-control form-control-sm"
                id="until"
                value={filter.until || ""}
                onChange={e => {
                  setFilter({
                    ...filter,
                    until: e.target.value,
                  })
                }}
              />
            </div>
          </div>
        </div>

        {/* Sector Filter */}
        <div className="mb-4">
          <label className="form-label fw-bold text-white">
            <i className="fa fa-tag me-2"></i>
            {t("Sector")}
          </label>
          <select
            className="form-select form-select-sm"
            id="sector"
            value={filter.sector}
            onChange={e => {
              setFilter({
                ...filter,
                sector: e.target.value,
              })
            }}
          >
            <option value="">{t("All Sectors")}</option>
            {sectors &&
              sectors.map((item, index) => {
                return (
                  <option
                    key={index}
                    value={item.name}
                  >{`${item.name} (${item.count})`}</option>
                )
              })}
          </select>
        </div>

        {/* Name Filter */}
        <div className="mb-4">
          <label className="form-label fw-bold text-white">
            <i className="fa fa-search me-2"></i>
            {t("Search by Name")}
          </label>
          <input
            type="text"
            className="form-control form-control-sm"
            id="name"
            placeholder={t("Enter place name...")}
            value={filter.name}
            onChange={e => {
              const target = e.target as HTMLInputElement
              setFilter({
                ...filter,
                name: target.value,
              })
            }}
          />
        </div>

        {/* Assigned To Filter */}
        <div className="mb-4">
          <label className="form-label fw-bold text-white">
            <i className="fa fa-user me-2"></i>
            {t("Assigned To")}
          </label>
          <select
            className="form-select form-select-sm"
            id="assignedTo"
            value={filter.assignedTo || ""}
            onChange={e => {
              setFilter({
                ...filter,
                assignedTo: e.target.value,
              })
            }}
          >
            <option value="">{t("All Users")}</option>
            {users &&
              users.map((item, index) => {
                return (
                  <option
                    key={index}
                    value={item.id}
                  >{`${item.firstName} ${item.lastName}`}</option>
                )
              })}
          </select>
        </div>

        {/* Stage Filter */}
        <div className="mb-4">
          <label className="form-label fw-bold text-white">
            <i className="fa fa-flag me-2"></i>
            {t("Stage")}
          </label>
          <select
            className="form-select form-select-sm"
            id="stageId"
            value={filter.stageId || ""}
            onChange={e => {
              setFilter({
                ...filter,
                stageId: e.target.value,
              })
            }}
          >
            <option value="">{t("All Stages")}</option>
            {stages &&
              stages.map((item, index) => {
                return (
                  <option key={index} value={item.id}>{item.name}</option>
                )
              })}
          </select>
        </div>

        {/* Category Filter */}
        <div className="mb-4">
          <label className="form-label fw-bold text-white">
            <i className="fa fa-layer-group me-2"></i>
            {t("Category")}
          </label>
          <select
            className="form-select form-select-sm"
            id="categoryId"
            value={filter.categoryId || ""}
            onChange={e => {
              setFilter({
                ...filter,
                categoryId: e.target.value,
              })
            }}
          >
            <option value="">{t("All Categories")}</option>
            {categories &&
              categories.map((item, index) => {
                return (
                  <option key={index} value={item.id}>{item.name}</option>
                )
              })}
          </select>
        </div>

        {/* Status Filter */}
        <div className="mb-4">
          <label className="form-label fw-bold text-white">
            <i className="fa fa-info-circle me-2"></i>
            {t("Status")}
          </label>
          <select
            className="form-select form-select-sm"
            id="placeStatusId"
            value={filter.placeStatusId || ""}
            onChange={e => {
              setFilter({
                ...filter,
                placeStatusId: e.target.value,
              })
            }}
          >
            <option value="">{t("All Statuses")}</option>
            {placeStatus &&
              placeStatus.map((item, index) => {
                return (
                  <option key={index} value={item.id}>{item.name}</option>
                )
              })}
          </select>
        </div>

        {/* Clear Filters Button */}
        <div className="mb-3">
          <div className="row g-2">
            <div className="col-12">
              <button
                className={`btn btn-outline-light btn-sm w-100 ${isMobile ? 'btn-lg' : ''}`}
                onClick={() => {
                  setFilter({
                    from: null,
                    until: null,
                    sector: "",
                    name: "",
                    stageId: null,
                    categoryId: null,
                    assignedTo: null,
                    placeStatusId: null,
                    withVehicleVisits: null,
                  })
                }}
                style={{ minHeight: isMobile ? '48px' : 'auto' }}
              >
                <i className="fa fa-eraser me-2"></i>
                {t("Clear Filters")}
              </button>
            </div>

          </div>
        </div>
      </div>
    )
  }
  if (!user) {
    window.location.href = Routes.SignIn.path
    return null
  }
  return (
    <>
      {showAddEditPlace ? (
        <AddEditPlace show={true} toggle={toggleAddEditPlace} />
      ) : null}
      {showAssignUsers ? (
        <AssignUser
          show={showAssignUsers}
          toggle={toggleAssignUsers}
          place={selectedPlace}
        />
      ) : null}
      {showPriority && (
        <PlacePriority show={showPriority} toggle={togglePriorityModal} />
      )}
      {/* HEADER */}

      {/* BODY */}
      <Card className="my-2 shadow-sm">
        <Card.Body className=" p-0">
          <Row className="mx-0 p-0">
            {/* FILTER */}
            {showFilter && (
              <Col
                xs={12}
                md={isMobile ? 12 : 3}
                lg={isMobile ? 12 : 2}
                className={`bg-dark text-light ${isMobile ? 'position-absolute' : ''}`}
                style={isMobile ? {
                  zIndex: 1000,
                  height: '100vh',
                  overflowY: 'auto',
                  top: 0,
                  left: 0,
                  right: 0
                } : {}}
              >
                <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
                  <h5 className="mb-0 text-white">
                    {t("Filters")}
                    {totalActiveFiltersCount > 0 && (
                      <span className="badge bg-warning text-dark ms-2">
                        {totalActiveFiltersCount}
                      </span>
                    )}
                  </h5>
                  <button
                    className="btn btn-outline-light btn-sm"
                    onClick={() => setShowFilter(false)}
                    style={{ minHeight: '40px', minWidth: '40px' }}
                  >
                    <i className="fa fa-times"></i>
                  </button>
                </div>
                <Row>
                  <Col style={{
                    overflowY: "auto",
                    height: isMobile ? "65vh" : "70vh",
                    padding: isMobile ? '1rem' : undefined
                  }}>
                    {filterUI()}
                    {mapMode === "drawPolygon" && (
                      <div className="mt-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <h6 className="text-white mb-0">{t("Polygon results")}</h6>
                          <span className="badge bg-light text-dark">{selectedPlaces.length}</span>
                        </div>
                        {ListOfPlacesUI(selectedPlaces, {
                          height: isMobile ? "34vh" : "30vh",
                          borderRadius: "8px",
                        })}
                      </div>
                    )}
                  </Col>
                </Row>
              </Col>
            )}
            <Col
              className="p-0 position-relative"
              xs={12}
              md={showFilter ? 9 : 12}
              lg={showFilter ? 10 : 12}
            >


              <>
                <div
                  style={{
                    position: "absolute",
                    top: "10px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    zIndex: 10,
                    background: "rgba(255,255,255,0.95)",
                    backdropFilter: "blur(4px)",
                    WebkitBackdropFilter: "blur(4px)",
                    borderRadius: "20px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
                    padding: "4px 8px",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <div className="btn-group btn-group-sm" role="group" aria-label="display-mode">
                    <button
                      type="button"
                      className={`btn ${isMapView ? "btn-primary" : "btn-outline-secondary"}`}
                      onClick={() => setDisplayMode("map")}
                      title={t("Map view")}
                    >
                      <i className="fa fa-map me-1"></i>
                      {t("Map")}
                    </button>
                    <button
                      type="button"
                      className={`btn ${!isMapView ? "btn-primary" : "btn-outline-secondary"}`}
                      onClick={() => setDisplayMode("list")}
                      title={t("Grid view")}
                    >
                      <i className="fa fa-th-large me-1"></i>
                      {t("Grid")}
                    </button>
                  </div>
                </div>
                <div className="position-relative" style={{ overflow: "visible" }}>
                  <div
                    className={`fab-backdrop ${quickActionsOpen ? "fab-backdrop-visible" : ""}`}
                    onClick={() => setQuickActionsOpen(false)}
                  ></div>
                  <button
                    type="button"
                    className="btn btn-light shadow-sm position-absolute"
                    onClick={() => setShowFilter(!showFilter)}
                    style={{
                      right: "16px",
                      top: isMobile ? "72px" : "18px",
                      zIndex: 1001,
                      borderRadius: "999px",
                      padding: "10px 14px",
                      border: "1px solid rgba(0,0,0,0.08)",
                    }}
                    title={t("Filter")}
                  >
                    <i className="fa fa-filter me-2"></i>
                    {t("Filter")}
                    {totalActiveFiltersCount > 0 && (
                      <span className="badge bg-warning text-dark ms-2">
                        {totalActiveFiltersCount}
                      </span>
                    )}
                  </button>
                  <div
                    className="fab-container"
                    ref={quickActionsRef}
                  >
                    <div className={`fab-menu ${quickActionsOpen ? "fab-menu-open" : ""}`}>
                      <button
                        className="fab-menu-item"
                        style={{ "--item-index": 1 } as React.CSSProperties}
                        onClick={() => {
                          toggleAddEditPlace()
                          setQuickActionsOpen(false)
                        }}
                      >
                        <span className="fab-menu-item-label">{t("Add New Place")}</span>
                        <span className="fab-menu-item-icon" style={{ backgroundColor: "#0d6efd" }}>
                          <i className="fa fa-plus"></i>
                        </span>
                      </button>
                      <button
                        className="fab-menu-item"
                        style={{ "--item-index": 0 } as React.CSSProperties}
                        onClick={() => {
                          toggleHistoryModal()
                          setQuickActionsOpen(false)
                        }}
                      >
                        <span className="fab-menu-item-label">{t("History")}</span>
                        <span className="fab-menu-item-icon" style={{ backgroundColor: "#8b5cf6" }}>
                          <i className="fa fa-history"></i>
                        </span>
                      </button>
                    </div>
                    <button
                      className={`fab-main ${quickActionsOpen ? "fab-main-active" : ""}`}
                      onClick={() => setQuickActionsOpen(!quickActionsOpen)}
                      aria-expanded={quickActionsOpen}
                      aria-haspopup="true"
                      title={t("Quick actions")}
                    >
                      <span className="fab-main-icon">
                        <i className={`fa ${quickActionsOpen ? "fa-times" : "fa-plus"}`}></i>
                      </span>
                    </button>
                  </div>
                  {/* Loading indicator for map view */}
                  {isLoading && (
                    <div
                      className="position-absolute top-0 start-50 translate-middle-x mt-3"
                      style={{ zIndex: 1000 }}
                    >
                      <div className="alert alert-info d-flex align-items-center shadow" role="alert">
                        <div className="spinner-border spinner-border-sm me-2" role="status">
                          <span className="visually-hidden">{t("Loading...")}</span>
                        </div>
                        <div>
                          <strong>{t("Loading places...")}</strong>
                          {totalRecords > 0 && (
                            <small className="d-block">
                              {records.length} / {totalRecords} {t("loaded")}
                            </small>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {isMapView ? (
                    mapProvider === "osm" ? (
                      <LeafletMainMap
                        records={showPoints ? visibleRecords : []}
                        geofences={state.Geofences}
                        vehicleRecords={recordsVehicles}
                        center={state.center}
                        zoom={zoom}
                        onMarkerClick={toggleDetails}
                        onVehicleClick={toggleDetails}
                        onGeofenceClick={goToGeofenceDetails}
                        onMapClick={(lat, lng) => handleClickOnMap({ lat, lng })}
                        mapMode={mapMode}
                        polygon={polygon}
                        isLoading={isLoading}
                        isMobile={isMobile}
                        totalRecordsWithCoords={records.filter(x => x.latitude > 0).length}
                        requestSize={requestSize}
                        style={{
                          width: "100%",
                          minHeight: isMobile ? "400px" : "300px",
                          height: isMobile ? "70vh" : "84vh",
                        }}
                      />
                    ) : (
                      <GoogleMapReact
                        ref={GoogleMapRef}
                        onClick={handleClickOnMap}
                        yesIWantToUseGoogleMapApiInternals={true}
                        onGoogleApiLoaded={({ map, maps }) =>
                          handleApiLoaded(map, maps)
                        }
                        bootstrapURLKeys={{
                          key: Settings.GoogleMapsKey
                        }}
                        style={{
                          width: "100%",
                          minHeight: isMobile ? "400px" : "300px",
                          height: isMobile ? "70vh" : "84vh",
                          position: "relative",
                        }}
                        center={{ lat: state.center.lat, lng: state.center.lng }}
                        defaultZoom={zoom}
                        options={{
                          gestureHandling: isMobile ? 'cooperative' : 'greedy',
                          zoomControl: false,
                          mapTypeControl: false,
                          streetViewControl: false,
                          fullscreenControl: false,
                          restriction: {
                            latLngBounds: {
                              north: 85,
                              south: -85,
                              west: -180,
                              east: 180
                            },
                            strictBounds: true
                          },
                          minZoom: 2,
                          maxZoom: isMobile ? 18 : 21
                        }}
                      >
                        {/* Render limited records for performance on mobile */}
                        {showPoints && visibleRecords
                          .filter(x => x.latitude > 0)
                          .slice(0, isMobile ? 50 : visibleRecords.length) // Limit markers on mobile
                          .map((item, index) => (
                            <PinComponent
                              Type={"Visit"}
                              lat={item.latitude}
                              key={`visit-${item.id || index}`}
                              item={item}
                              toggle={toggleDetails}
                              lng={item.longitude}
                              text={item.name}
                            />
                          ))}
                        {recordsVehicles
                          .filter(x => x.latitud > 0)
                          .slice(0, isMobile ? 20 : recordsVehicles.length) // Limit vehicle markers on mobile
                          .map((item, index) => (
                            <PinComponent
                              Type={"vehicle"}
                              lat={item.latitud}
                              key={`vehicle-${item.id || index}`}
                              item={item}
                              toggle={toggleDetails}
                              lng={item.longitud}
                              text={[item?.coche?.marca, item?.coche?.modelo, item?.coche?.ficha]
                                .filter(Boolean)
                                .join(" ")}
                            />
                          ))}

                        {(state.Geofences as GeofenceRecord[])
                          .filter(
                            geofence =>
                              geofence.centroidLatitude &&
                              geofence.centroidLongitude
                          )
                          .map(geofence => (
                            <GeofenceMarkerLabel
                              key={`geofence-label-${geofence.id}`}
                              lat={geofence.centroidLatitude}
                              lng={geofence.centroidLongitude}
                              onClick={() => goToGeofenceDetails(geofence)}
                              name={geofence.name}
                            />
                          ))}

                        {/* Indicador de lugares cargados en el mapa */}
                        {!isLoading && records.length > 0 && (
                          <div
                            style={{
                              position: 'absolute',
                              top: '10px',
                              left: '10px',
                              background: 'rgba(255, 255, 255, 0.95)',
                              color: '#333',
                              padding: '10px 15px',
                              borderRadius: '8px',
                              fontSize: '13px',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                              border: '1px solid rgba(0,0,0,0.1)',
                              zIndex: 10
                            }}
                          >
                            <div className="d-flex align-items-center">
                              <i className="fa fa-map-marker-alt text-primary me-2"></i>
                              <div>
                                <strong>{records.filter(x => x.latitude > 0).length}</strong> {t("locations")}
                                <small className="d-block text-muted">
                                  {`${(state.Geofences as GeofenceRecord[]).length} ${t("geofences")}`}
                                </small>
                                {process.env.NODE_ENV === 'development' && requestSize && (
                                  <small className="text-muted d-block" style={{ fontSize: '11px' }}>
                                    {requestSize}
                                  </small>
                                )}
                              </div>
                            </div>
                          </div>
                        )}

                        {isMobile && visibleRecords.length > 50 && (
                          <div
                            style={{
                              position: 'absolute',
                              top: '10px',
                              left: '10px',
                              background: 'rgba(0,0,0,0.7)',
                              color: 'white',
                              padding: '8px 12px',
                              borderRadius: '4px',
                              fontSize: '12px'
                            }}
                          >
                            {t("Showing")} 50 {t("of")} {visibleRecords.length} {t("locations")}
                          </div>
                        )}
                      </GoogleMapReact>
                    )
                  ) : (
                    <div
                      className="bg-light border-top"
                      style={{
                        minHeight: isMobile ? "400px" : "84vh",
                        maxHeight: isMobile ? "none" : "84vh",
                        overflow: "hidden",
                      }}
                    >
                      {ListOfPlacesUI(visibleRecords, {
                        // height: "auto",
                        backgroundColor: "transparent",
                      })}
                    </div>
                  )}
                </div>
              </>

              <Row className="mx-0">
                <Col xs={12} className="mt-2" >
                  <SummaryByStage filter={filter} />

                  <SummaryByStatus filter={filter} />

                  <SummaryVehicleVisits />
                </Col>

              </Row>
            </Col>
          </Row>
        </Card.Body>
      </Card >

      {/* VisitHistory Sidebar - Optimized lateral view */}
      <VisitHistorySidebar
        show={showHistory}
        toggle={toggleHistoryModal}
        placement="end"
        allowMapInteraction={true}
      />
    </>
  )
}

export default Visits
